import { ReactLenis } from "lenis/react";
import "lenis/dist/lenis.css";
import type { ReactNode } from "react";
import { useMemo } from "react";
import { useLocation } from "react-router-dom";

const LENIS_PROFILES = {
  subtle: {
    lerp: 0.1,
    smoothWheel: true,
    syncTouch: false,
    wheelMultiplier: 1,
    touchMultiplier: 1,
    overscroll: true,
    autoRaf: true,
    anchors: { offset: 0 },
  },
  glide: {
    // EXAGGERATED x10 - extreme cinematic glide for clear A/B testing.
    // Very long duration + heavy easing = obvious "floaty" Lenis feel.
    duration: 4.5,
    easing: (t: number) => (t === 1 ? 1 : 1 - Math.pow(2, -12 * t)),
    smoothWheel: true,
    syncTouch: false,
    wheelMultiplier: 2.4,
    touchMultiplier: 1,
    overscroll: true,
    autoRaf: true,
    anchors: { offset: 0 },
  },
} as const;

type ScrollProfile = keyof typeof LENIS_PROFILES;
const DEFAULT_SCROLL_PROFILE: ScrollProfile = "glide";

interface SmoothScrollProps {
  children: ReactNode;
}

/**
 * Official site-wide Lenis React root integration.
 *
 * Scroll context:
 * - root=true => Lenis controls the main page scroll (window/html/body)
 * - no nested custom scroll container is introduced
 * - touch stays native for correct mobile/tablet feel
 *
 * Test profiles:
 * - ?scroll=subtle  => conservative premium smoothing
 * - ?scroll=glide   => stronger Apple-style glide
 */
const SmoothScroll = ({ children }: SmoothScrollProps) => {
  const { search } = useLocation();

  const prefersReduced =
    typeof window !== "undefined" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  const activeProfile = useMemo<ScrollProfile>(() => {
    const value = new URLSearchParams(search).get("scroll");
    return value === "subtle" || value === "glide"
      ? value
      : DEFAULT_SCROLL_PROFILE;
  }, [search]);

  if (prefersReduced) return <>{children}</>;

  return (
    <ReactLenis root options={LENIS_PROFILES[activeProfile]}>
      {children}
    </ReactLenis>
  );
};

export { LENIS_PROFILES, DEFAULT_SCROLL_PROFILE };
export default SmoothScroll;
